import {useCallback, useEffect, useState, useRef} from 'react';
import {useTranslation} from 'react-i18next';

import {MediaDeviceWrapper, notifier} from 'components';
import {DeviceKind} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {logger, mediaDevicesUtils} from 'utils';

import {DataVisualization} from './DataVisualization';

type Props = {
    value: string;
    devices: MediaDeviceInfo[];
    onChange: (deviceId: string) => void;
};

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.MEDIA_DEVICES_MODAL;
const FRAME_SIZES = {width: 360, height: 50};

export const AudioInputDevice: React.FC<Props> = ({value, devices, onChange}) => {
    const {t} = useTranslation();

    const [audioStream, setAudioStream] = useState<MediaStream>(null);
    const [devicePermissionsRequired, setDevicePermissionsRequired] = useState(false);
    const [audioData, setAudioData] = useState(new Uint8Array(0));
    const requestAnimationFrameRef = useRef(0);
    const analyser = useRef(null);
    const distortion = useRef(null);

    const requestDevicePermissions = useCallback(() =>
        navigator.mediaDevices.getUserMedia({
            audio: {deviceId: {ideal: value}},
            video: false
        }), [value]);

    const handleChangeInputDevice = useCallback((deviceId: string) => {
        setDevicePermissionsRequired(false);
        setAudioData(new Uint8Array(0));
        onChange(deviceId);
    }, [onChange]);

    const tick = useCallback(() => {
        const dataArray = new Uint8Array(analyser.current.frequencyBinCount);
        analyser.current.getByteFrequencyData(dataArray);
        setAudioData(dataArray);
        requestAnimationFrameRef.current = requestAnimationFrame(tick);
    }, [analyser]);

    useEffect(() => {
        if (audioStream) {
            const audioContext = new window.AudioContext();
            analyser.current = audioContext.createAnalyser();
            analyser.current.fftSize = 256;
            const source = audioContext.createMediaStreamSource(audioStream);
            source.connect(analyser.current);
            distortion.current = audioContext.createWaveShaper();
            analyser.current.connect(distortion.current);
            distortion.current.connect(audioContext.destination);
            requestAnimationFrameRef.current = requestAnimationFrame(tick);
            return () => {
                cancelAnimationFrame(requestAnimationFrameRef.current);
                analyser.current.disconnect();
                distortion.current.disconnect();
                source.disconnect();
            };
        }
    }, [audioStream, tick]);

    useEffect(() => {
        if (!devicePermissionsRequired) {
            requestDevicePermissions()
                .then((stream) => {
                    setAudioStream(stream);
                    setDevicePermissionsRequired(true);
                })
                .catch((error) => {
                    logger.error(error);
                    notifier.error({title: t(T_KEYS.ERROR_TITLE), details: error.message});
                });
        }
    }, [requestDevicePermissions, devicePermissionsRequired, t]);

    useEffect(() => () => {
        mediaDevicesUtils.cleanMediaStream(audioStream);
    }, [audioStream]);

    return (
        <MediaDeviceWrapper
            type={DeviceKind.Microphone}
            devices={devices}
            selectedDeviceId={value}
            onDeviceChange={handleChangeInputDevice}
        >
            <div className='tw-flex tw-items-center tw-justify-between'>
                <DataVisualization className='tw-px-1 tw-w-full tw-mt-8' audioData={audioData} frameSizes={FRAME_SIZES} />
            </div>
        </MediaDeviceWrapper>
    );
};